import { useState } from "react";
import { Mail, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { getSessionId } from "@/lib/flow";
import { getUtm } from "@/lib/tracking";
import { ShareReady } from "./ShareReady";

const CONSENT = "Acconsento al trattamento dei dati per ricevere la mia routine e comunicazioni FineSkin";
const saved = () => { try { return JSON.parse(localStorage.getItem("fs_lead")) || {}; } catch { return {}; } };

export const normalizePhone = (raw) => {
  const t = raw.trim();
  let d = t.replace(/\D/g, "");
  if (t.startsWith("00")) d = d.slice(2);
  else if (!t.startsWith("+") && d.length <= 10) d = `39${d}`;
  return d;
};

const ADDON_NOTE = {
  tonepad: (step) => `Un gesto in più accanto a ${step}: passa il pad su viso pulito 2–3 sere a settimana al posto del tonico, nelle altre sere continua con il tonico.`,
  patch: (step) => `Un gesto in più accanto a ${step}: applica i patch 2–3 volte a settimana prima del contorno occhi.`,
  boost: (step) => `Un gesto in più accanto a ${step}: 2–3 volte a settimana, la sera, dopo il trattamento.`,
  device: (step) => `Un gesto in più accanto a ${step}: usalo subito dopo il prodotto di questo step per amplificarne l'effetto.`,
};

const snapshot = (routine, products, chosen, extras = []) => Object.fromEntries(["day", "night"].map((m) => [m,
  routine[m].flatMap((e) => {
    const pid = chosen(m, e);
    if (e.extra) {
      const info = e.extra_info?.[pid] || { frequency: e.frequency, how: e.how_to_insert };
      return [{ step_key: e.step_key, step_name: `${e.step_label} · ${e.step_name}`, product_id: pid, kind: "extra", note: `${info.frequency}. ${info.how}` }];
    }
    const main = { step_key: e.step_key, step_name: e.step_name, product_id: pid, kind: "step" };
    const stepName = products[pid]?.name || e.step_name.toLowerCase();
    const adds = (e.addon_ids || []).filter((id) => extras.includes(id) && products[id])
      .map((id) => ({ step_key: `${e.step_key}_extra`, step_name: `${e.step_name} · in più`, product_id: id, kind: "addon", note: (ADDON_NOTE[e.addon_kind] || ADDON_NOTE.device)(stepName) }));
    return [main, ...(m === "day" || !routine.day.some((d) => d.step_key === e.step_key) ? adds : [])];
  }).filter((e) => e.product_id && products[e.product_id])]));

const isMobileDevice = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);

export const ShareRoutine = (props) => {
  const [channel, setChannel] = useState(null);
  const [form, setForm] = useState(() => ({ name: "", phone: "", email: "", consent: false, ...saved() }));
  const [busy, setBusy] = useState(false);
  const [lead, setLead] = useState(null);
  const [mobile] = useState(isMobileDevice);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isWa = channel === "whatsapp";
  const phone = normalizePhone(form.phone);

  const close = () => { setChannel(null); setLead(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (isWa && phone.length < 8) { toast.error("Inserisci un numero WhatsApp valido, con prefisso (es. +39)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/leads", {
        session_id: getSessionId(), name: form.name, channel, phone: isWa ? `+${phone}` : form.phone, email: form.email, consent: form.consent,
        utm: getUtm(), products: props.cartItems, total: props.total, cart_url: props.cartUrl,
        routine: snapshot(props.routine, props.products, props.chosen, props.extras), needs: (props.profile.needs || []).map((n) => n.name),
      });
      localStorage.setItem("fs_lead", JSON.stringify({ name: form.name, phone: form.phone, email: form.email, consent: true }));
      setLead(r.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const inputCls = "w-full mt-2 px-4 py-3 bg-[#FAF8F5] border border-[#E6DFD5] text-sm outline-none focus:border-[#C5A059]";

  return (
    <section className="mt-14 border border-[#E6DFD5] bg-white p-6 sm:p-8" data-testid="share-routine">
      <p className="fs-eyebrow">Portala con te</p>
      <h2 className="font-serif text-2xl sm:text-3xl mt-3">Ricevi la tua routine</h2>
      <p className="text-sm text-[#595652] mt-3 font-light leading-relaxed max-w-md" data-testid="share-intro">
        {mobile ? "Ricevi il tuo rituale completo di foto e descrizioni in PDF, direttamente sul tuo WhatsApp o via email."
          : "Ricevi il tuo rituale completo di foto e descrizioni in PDF, direttamente nella tua email."}
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        {mobile && (
          <button data-testid="share-whatsapp-btn" onClick={() => setChannel("whatsapp")} className="fs-btn justify-center">
            <MessageCircle size={16} strokeWidth={1.5} /> Ricevi su WhatsApp
          </button>
        )}
        <button data-testid="share-email-btn" onClick={() => setChannel("email")} className={`${mobile ? "fs-btn-ghost" : "fs-btn"} justify-center`}>
          <Mail size={16} strokeWidth={1.5} /> Ricevi via email
        </button>
      </div>

      <Dialog open={!!channel} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="lead-dialog" className="bg-[#FAF8F5] border-[#E6DFD5] sm:max-w-md p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6">
            <p className="fs-eyebrow">{isWa ? "WhatsApp" : "Email"}</p>
            <DialogTitle className="font-serif text-2xl font-normal mt-2">{lead ? "Ecco la tua routine" : "A chi inviamo la routine?"}</DialogTitle>
            <DialogDescription className="text-sm text-[#595652]">
              {lead ? "Completa di foto e descrizioni di ogni prodotto." : `Lasciaci i tuoi dati: ${isWa ? (props.waCloud ? "riceverai il PDF della routine dal numero FineSkin." : "ti invierai la routine sul tuo WhatsApp con un tocco.") : "riceverai un'email FineSkin con il PDF della routine."}`}
            </DialogDescription>
          </DialogHeader>
          {lead ? (
            <ShareReady lead={lead} channel={channel} profile={props.profile} count={props.cartItems.length} total={props.total} phone={phone} onDone={close} waCloud={props.waCloud} />
          ) : (
            <form onSubmit={submit} className="px-6 pb-6 pt-2 space-y-4" data-testid="lead-form">
              <label className="block text-xs tracking-[0.14em] uppercase text-[#8C8275]">Nome e cognome
                <input data-testid="lead-name-input" required value={form.name} onChange={(e) => set("name")(e.target.value)} className={inputCls} placeholder="Es. Giulia Rossi" />
              </label>
              {isWa ? (
                <label className="block text-xs tracking-[0.14em] uppercase text-[#8C8275]">Numero WhatsApp
                  <input data-testid="lead-phone-input" required type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className={inputCls} placeholder="+39 333 1234567" />
                </label>
              ) : (
                <label className="block text-xs tracking-[0.14em] uppercase text-[#8C8275]">Email
                  <input data-testid="lead-email-input" required type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className={inputCls} placeholder="nome@esempio.it" />
                </label>
              )}
              <label className="flex items-start gap-3 text-xs text-[#595652] leading-relaxed cursor-pointer">
                <Checkbox data-testid="lead-consent-checkbox" checked={form.consent} onCheckedChange={(v) => set("consent")(!!v)} className="mt-0.5 border-[#C5A059] data-[state=checked]:bg-[#1A1A18] data-[state=checked]:border-[#1A1A18]" />
                <span>{CONSENT}</span>
              </label>
              <button data-testid="lead-submit-btn" type="submit" disabled={busy || !form.consent} className="fs-btn w-full justify-center">
                {busy ? "Preparo la tua routine…" : "Prepara la mia routine"} <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
